import { useCallback, useState } from "react"
import styled from "styled-components"

import { UITypes } from "types"

import { getDateRangeFromOption } from "utils/dateUtils"
import { DateRangePickerSelect } from "components/common/DateRangePickerSelect"
import { allDateSelectOptions } from "pages/vendor/Dashboard/constants"

import { DateRange, DateRangeContext } from "./DateRangeContext"

const Section = styled.section`
  width: 100%;
  padding: 24px 32px;
  background: #f8fafc;
  border-radius: 40px;
  margin-top: 24px;

  @media only screen and (max-width: 768px) {
    padding: 24px 16px;
    margin-top: 16px;
  }
`

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-left: 16px;
`

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 500;
  line-height: 1;
  margin: 0;
`

const DashboardWidgetContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  > div {
    flex: 1 0 450px;
    min-width: 45%;
    max-width: initial;

    @media only screen and (max-width: 768px) {
      flex: 1 0 100%;
    }
  }
`

interface DashboardWidgetSectionProps {
  children: React.ReactNode
  title: React.ReactNode
  defaultTimeRange?: UITypes.Option
  withDateRangePicker?: boolean
  dateRange?: DateRange
  onSelectDateRange?: (dateRange: DateRange) => void
}

export const DashboardWidgetSection = ({
  children,
  title,
  withDateRangePicker = false,
  defaultTimeRange = allDateSelectOptions[0],
  dateRange: controlledDateRange,
  onSelectDateRange,
}: DashboardWidgetSectionProps) => {
  const [internalDateRange, setInternalDateRange] = useState<DateRange>(() =>
    getDateRangeFromOption(String(defaultTimeRange.value)),
  )

  const dateRange = controlledDateRange ?? internalDateRange
  const handleSelectDateRange = useCallback(
    (newDateRange: DateRange) => {
      if (onSelectDateRange) {
        onSelectDateRange(newDateRange)
      } else {
        setInternalDateRange(newDateRange)
      }
    },
    [onSelectDateRange],
  )

  return (
    <DateRangeContext.Provider value={{ dateRange }}>
      <Section>
        <SectionHeader>
          {title && <SectionTitle>{title}</SectionTitle>}
          {withDateRangePicker && (
            <DateRangePickerSelect
              options={allDateSelectOptions}
              defaultOption={defaultTimeRange}
              onSelectDateRange={handleSelectDateRange}
            />
          )}
        </SectionHeader>
        <DashboardWidgetContainer>{children}</DashboardWidgetContainer>
      </Section>
    </DateRangeContext.Provider>
  )
}
